import React from 'react';
import { Field, Section, StringList } from './Fields';

// LangEditor: renders editable fields for a single language of the site config.
const LangEditor = ({ lang, config, setConfig }) => {
  if (!config) return null;

  const patch = (path, value) => {
    // path is an array of keys; deep update immutably
    setConfig((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let ref = next;
      for (let i = 0; i < path.length - 1; i++) {
        if (ref[path[i]] === undefined) ref[path[i]] = {};
        ref = ref[path[i]];
      }
      ref[path[path.length - 1]] = value;
      return next;
    });
  };

  const hero = config.hero?.[lang] || {};
  const nav = config.nav?.[lang] || {};
  const steps = config.steps?.[lang] || { items: [] };
  const cs = config.circuitsSection?.[lang] || {};
  const contact = config.contactSection?.[lang] || {};
  const footer = config.footer?.[lang] || {};
  const modal = config.modal?.[lang] || {};

  return (
    <div className="space-y-6">
      <Section title="Navigation">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Nos excursions" value={nav.excursions} onChange={(v) => patch(['nav', lang, 'excursions'], v)} />
          <Field label="Comment ça marche" value={nav.how} onChange={(v) => patch(['nav', lang, 'how'], v)} />
          <Field label="Réserver" value={nav.book} onChange={(v) => patch(['nav', lang, 'book'], v)} />
        </div>
      </Section>

      <Section title="Hero (section d'accueil)">
        <Field label="Tagline (au-dessus du titre)" value={hero.tagline} onChange={(v) => patch(['hero', lang, 'tagline'], v)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Titre - 1ère partie" value={hero.title_1} onChange={(v) => patch(['hero', lang, 'title_1'], v)} />
          <Field label="Titre - partie en italique doré" value={hero.title_2} onChange={(v) => patch(['hero', lang, 'title_2'], v)} />
        </div>
        <Field label="Description" value={hero.lead} onChange={(v) => patch(['hero', lang, 'lead'], v)} textarea rows={3} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="CTA principal" value={hero.cta_primary} onChange={(v) => patch(['hero', lang, 'cta_primary'], v)} />
          <Field label="CTA WhatsApp" value={hero.cta_whatsapp} onChange={(v) => patch(['hero', lang, 'cta_whatsapp'], v)} />
        </div>
        <Field label="Titre timeline" value={hero.journey} onChange={(v) => patch(['hero', lang, 'journey'], v)} />
        <div className="grid grid-cols-3 gap-3">
          <Field label="Début" value={hero.journey_start} onChange={(v) => patch(['hero', lang, 'journey_start'], v)} />
          <Field label="Milieu" value={hero.journey_mid} onChange={(v) => patch(['hero', lang, 'journey_mid'], v)} />
          <Field label="Fin" value={hero.journey_end} onChange={(v) => patch(['hero', lang, 'journey_end'], v)} />
        </div>
        <StringList label="Badges (points du bandeau)" value={hero.badges || []} onChange={(v) => patch(['hero', lang, 'badges'], v)} />
      </Section>

      <Section title="Le déroulé (4 étapes)">
        <Field label="Surtitre" value={steps.overline} onChange={(v) => patch(['steps', lang, 'overline'], v)} />
        <Field label="Titre" value={steps.title} onChange={(v) => patch(['steps', lang, 'title'], v)} />
        <Field label="Description" value={steps.lead} onChange={(v) => patch(['steps', lang, 'lead'], v)} textarea rows={2} />
        <div className="space-y-3">
          {(steps.items || []).map((it, i) => (
            <div key={i} className="rounded-md border border-white/5 p-3 bg-black/20">
              <div className="text-[10px] tracking-[0.2em] uppercase text-white/40 mb-2">Étape {String(i + 1).padStart(2, '0')}</div>
              <Field label="Titre" value={it.title} onChange={(v) => patch(['steps', lang, 'items', i, 'title'], v)} />
              <div className="mt-2" />
              <Field label="Texte" value={it.text} onChange={(v) => patch(['steps', lang, 'items', i, 'text'], v)} textarea rows={2} />
            </div>
          ))}
        </div>
      </Section>

      <Section title="Section circuits (en-tête + labels)">
        <Field label="Surtitre" value={cs.overline} onChange={(v) => patch(['circuitsSection', lang, 'overline'], v)} />
        <Field label="Titre" value={cs.title} onChange={(v) => patch(['circuitsSection', lang, 'title'], v)} />
        <Field label="Description" value={cs.lead} onChange={(v) => patch(['circuitsSection', lang, 'lead'], v)} textarea rows={2} />
        <div className="grid grid-cols-3 gap-3">
          <Field label='Bouton "Réserver"' value={cs.book} onChange={(v) => patch(['circuitsSection', lang, 'book'], v)} />
          <Field label='Bouton "Voir le détail"' value={cs.details} onChange={(v) => patch(['circuitsSection', lang, 'details'], v)} />
          <Field label='Suffixe prix' value={cs.per} onChange={(v) => patch(['circuitsSection', lang, 'per'], v)} placeholder="/ pers." />
        </div>
      </Section>

      <Section title="Popup circuits (labels)">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Titre 'Etapes'" value={modal.stops_title} onChange={(v) => patch(['modal', lang, 'stops_title'], v)} />
          <Field label="Bouton fermer" value={modal.close} onChange={(v) => patch(['modal', lang, 'close'], v)} />
          <Field label="Bouton WhatsApp" value={modal.book_whatsapp} onChange={(v) => patch(['modal', lang, 'book_whatsapp'], v)} />
        </div>
      </Section>

      <Section title="Contact (section)">
        <Field label="Surtitre" value={contact.overline} onChange={(v) => patch(['contactSection', lang, 'overline'], v)} />
        <Field label="Titre" value={contact.title} onChange={(v) => patch(['contactSection', lang, 'title'], v)} />
        <Field label="Description" value={contact.lead} onChange={(v) => patch(['contactSection', lang, 'lead'], v)} textarea rows={2} />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Label WhatsApp" value={contact.whatsappLabel} onChange={(v) => patch(['contactSection', lang, 'whatsappLabel'], v)} />
          <Field label="Label Téléphone" value={contact.phoneLabel} onChange={(v) => patch(['contactSection', lang, 'phoneLabel'], v)} />
          <Field label="Label Email" value={contact.emailLabel} onChange={(v) => patch(['contactSection', lang, 'emailLabel'], v)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field label="Label lieu" value={contact.locationLabel} onChange={(v) => patch(['contactSection', lang, 'locationLabel'], v)} />
          <Field label="Lieu (ligne 1)" value={contact.locationValue} onChange={(v) => patch(['contactSection', lang, 'locationValue'], v)} />
          <Field label="Lieu (ligne 2)" value={contact.locationExtra} onChange={(v) => patch(['contactSection', lang, 'locationExtra'], v)} />
        </div>
      </Section>

      <Section title="Footer">
        <Field label="Ligne de description" value={footer.brand_line} onChange={(v) => patch(['footer', lang, 'brand_line'], v)} textarea rows={2} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Label langues" value={footer.available} onChange={(v) => patch(['footer', lang, 'available'], v)} />
          <Field label="Copyright" value={footer.rights} onChange={(v) => patch(['footer', lang, 'rights'], v)} />
        </div>
      </Section>

      <Section title="Contenu des circuits (par langue)" description="Pour chaque circuit, activez ou désactivez la langue et éditez badge / titre / sous-titre / étapes / description (popup).">
        <div className="space-y-4">
          {(config.circuits || []).map((c, ci) => {
            const tr = c.translations?.[lang] || {};
            const enabled = (c.availableLangs || []).includes(lang);
            const toggle = () => {
              const cur = c.availableLangs || [];
              const next = cur.includes(lang) ? cur.filter((l) => l !== lang) : [...cur, lang];
              patch(['circuits', ci, 'availableLangs'], next);
            };
            return (
              <div key={c.id} className="rounded-md border border-white/10 p-4 bg-black/20">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] tracking-[0.2em] uppercase text-white/60">
                    Circuit · <span className="text-[#e0b972]">{c.id}</span>
                  </div>
                  <label className="inline-flex items-center gap-2 text-[12px] text-white/70 cursor-pointer">
                    <input type="checkbox" checked={enabled} onChange={toggle} className="accent-[#e89142]" />
                    Affiché en {lang.toUpperCase()}
                  </label>
                </div>
                {enabled && (
                  <div className="mt-3 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Field label="Badge" value={tr.badge} onChange={(v) => patch(['circuits', ci, 'translations', lang, 'badge'], v)} />
                      <Field label="Titre" value={tr.title} onChange={(v) => patch(['circuits', ci, 'translations', lang, 'title'], v)} />
                    </div>
                    <Field label="Sous-titre" value={tr.subtitle} onChange={(v) => patch(['circuits', ci, 'translations', lang, 'subtitle'], v)} />
                    <StringList label="Étapes (points du circuit)" value={tr.stops || []} onChange={(v) => patch(['circuits', ci, 'translations', lang, 'stops'], v)} />
                    <Field label="Description (affichée dans la popup)" value={tr.description} onChange={(v) => patch(['circuits', ci, 'translations', lang, 'description'], v)} textarea rows={5} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      <Section title="Rôles des contacts (par langue)">
        {(config.contacts || []).map((c, ci) => (
          <div key={c.id} className="rounded-md border border-white/10 p-3 bg-black/20 space-y-2">
            <div className="text-[11px] tracking-[0.2em] uppercase text-white/50">{c.name}</div>
            <Field label={`Rôle / Description (${lang.toUpperCase()})`} value={c.roles?.[lang]} onChange={(v) => patch(['contacts', ci, 'roles', lang], v)} />
          </div>
        ))}
      </Section>
    </div>
  );
};

export default LangEditor;
